import * as React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  Logout01Icon,
  Settings02Icon,
  User03Icon,
  ArrowUp01Icon,
  Cancel01Icon,
  BookOpen01Icon,
} from '@hugeicons/core-free-icons'
import { searchNavigation, type Role } from '@/lib/navigation'
import { Button } from '@/components/ui/button'
import { NavMain } from '@/components/nav-main'

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarTrigger,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { BrandLogo } from '@/components/brand-logo'
import { BranchSwitcher } from '@/components/branch-switcher'
import { useAuth } from '@/contexts/AuthContext'

const ROLE_LABELS: Record<Role, string> = {
  admin: 'Administrador',
  teacher: 'Profesor',
  staff: 'Personal',
}

export function AppSidebar({
  className,
  ...props
}: React.ComponentProps<typeof Sidebar>) {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { setOpenMobile, isMobile } = useSidebar()
  const role = (user?.role || 'staff') as Role

  const initials = user
    ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
    : 'U'

  const visibleGroups = searchNavigation(role, '')

  const closeMenu = () => {
    setOpenMobile(false)
  }

  const handleLogout = async () => {
    await logout()
  }

  return (
    <Sidebar variant="inset" className={className} {...props}>
      <SidebarHeader className="gap-3 px-3 pb-3 pt-5">
        <div className="flex items-center justify-between gap-3">
          <Link
            to="/"
            onClick={closeMenu}
            aria-label="St. George, ir al inicio"
            className="focus-visible:outline-2 focus-visible:outline-ring"
          >
            <BrandLogo className="w-40" />
          </Link>
          {isMobile ? (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={closeMenu}
              aria-label="Cerrar menú"
            >
              <HugeiconsIcon icon={Cancel01Icon} />
            </Button>
          ) : <SidebarTrigger aria-label="Ocultar menú" />}
        </div>
        <BranchSwitcher />
      </SidebarHeader>

      <SidebarContent>
        <nav id="sidebar-navigation" aria-label="Navegación principal">
          <NavMain
            groups={visibleGroups}
            onNavigate={closeMenu}
          />
        </nav>
      </SidebarContent>

      <SidebarFooter className="p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <Link to="/docs" onClick={closeMenu}>
                <HugeiconsIcon icon={BookOpen01Icon} aria-hidden="true" />
                <span>Ayuda</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <Avatar className="size-8">
                    {user?.profilePicture ? (
                      <AvatarImage
                        src={user.profilePicture}
                        alt={user.fullName}
                      />
                    ) : null}
                    <AvatarFallback className="bg-primary text-xs font-medium text-primary-foreground">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">
                      {user?.fullName || 'Usuario'}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {ROLE_LABELS[role]}
                    </span>
                  </div>
                  <HugeiconsIcon
                    icon={ArrowUp01Icon}
                    className="ml-auto size-4"
                  />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side={isMobile ? 'bottom' : 'right'}
                align="end"
                sideOffset={8}
                className="w-56"
              >
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm font-medium">
                      {user?.fullName}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {user?.email}
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem
                    onSelect={() => {
                      closeMenu()
                      navigate('/profile')
                    }}
                  >
                    <HugeiconsIcon icon={User03Icon} />
                    Perfil
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => {
                      closeMenu()
                      navigate('/profile?tab=security')
                    }}
                  >
                    <HugeiconsIcon icon={Settings02Icon} />
                    Contraseña y seguridad
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={handleLogout}
                  className="text-destructive focus:text-destructive"
                >
                  <HugeiconsIcon icon={Logout01Icon} />
                  Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
